import { useEffect, useRef, useCallback } from 'react'

const POLL_INTERVAL = 3000
const STORAGE_KEY = 'myagent_async_tasks'
const MAX_TASK_AGE = 30 * 60 * 1000 // 30 minutes — auto-expire stale tasks
const MAX_CONSECUTIVE_ERRORS = 10

/**
 * useAsyncTaskTracker Hook
 *
 * Polls getTaskStatus for async LLM role tasks and injects progress/result messages into chat.
 *
 * @param {Object} options
 * @param {Function} options.forwardCallFn - (roleId, method, params) => Promise — roleCall from appContext
 * @param {Function} options.onMessage - (msg) => void — appends a message to chat
 * @returns {{ track: (taskId, { roleId, displayName }) => void }}
 */
const DEFAULT_STORAGE = {
  getItem: (k) => localStorage.getItem(k),
  setItem: (k, v) => localStorage.setItem(k, v),
}

export const useAsyncTaskTracker = ({ forwardCallFn, onMessage, ready = true, storage = DEFAULT_STORAGE }) => {
  const tasksRef = useRef({}) // { [taskId]: { roleId, displayName, outputIndex, startedAt, errors } }
  const intervalsRef = useRef({})
  const forwardCallRef = useRef(forwardCallFn)
  const onMessageRef = useRef(onMessage)
  const storageRef = useRef(storage)
  forwardCallRef.current = forwardCallFn
  onMessageRef.current = onMessage
  storageRef.current = storage

  const persist = useCallback(() => {
    storageRef.current.setItem(STORAGE_KEY, JSON.stringify(tasksRef.current))
  }, [])

  const stopPolling = useCallback((taskId) => {
    clearInterval(intervalsRef.current[taskId])
    delete intervalsRef.current[taskId]
    delete tasksRef.current[taskId]
    persist()
  }, [persist])

  const poll = useCallback(async (taskId) => {
    const meta = tasksRef.current[taskId]
    if (!meta) return

    // Auto-expire stale tasks
    if (Date.now() - meta.startedAt > MAX_TASK_AGE) {
      onMessageRef.current({
        role: 'participant',
        participantId: meta.roleId,

        content: 'Task expired (no response)',
        timestamp: Date.now(),
        metadata: { taskId, type: 'failed', conversationId: meta.conversationId },
      })
      stopPolling(taskId)
      return
    }

    try {
      const status = await forwardCallRef.current(meta.roleId, 'getTaskStatus', [{ taskId }])
      meta.errors = 0 // reset on success

      // Emit accumulated output as a single updating message
      if (status.output?.length > 0 && status.output.length > meta.outputIndex) {
        const content = status.output
          .map(e => typeof e === 'string' ? e : (e.content || JSON.stringify(e)))
          .join('\n\n')
        onMessageRef.current({
          role: 'participant',
          participantId: meta.roleId,
          content,
          timestamp: meta.firstOutputAt || Date.now(),
          metadata: { taskId, type: 'progress', displayName: meta.displayName, conversationId: meta.conversationId },
        })
        if (!meta.firstOutputAt) meta.firstOutputAt = Date.now()
        meta.outputIndex = status.output.length
        persist()
      }

      // Terminal states
      if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
        // If completed and result already shown via output progress, skip the redundant message entirely
        const resultStr = status.result && (typeof status.result === 'string' ? status.result : JSON.stringify(status.result))
        const lastOutput = status.output?.[status.output.length - 1]
        const lastOutputStr = lastOutput && (typeof lastOutput === 'string' ? lastOutput : lastOutput.content)
        const resultAlreadyShown = status.status === 'completed' && resultStr && lastOutputStr && resultStr === lastOutputStr && meta.outputIndex >= status.output.length

        const content = resultAlreadyShown
          ? ''
          : status.status === 'completed'
            ? `Task completed${resultStr ? ': ' + resultStr : ''}`
            : `Task ${status.status}: ${status.error || 'Unknown'}`

        onMessageRef.current({
          role: 'participant',
          participantId: meta.roleId,
          content,
          timestamp: resultAlreadyShown ? (meta.firstOutputAt || Date.now()) : Date.now(),
          metadata: { taskId, type: status.status, result: status.result, conversationId: meta.conversationId },
        })
        stopPolling(taskId)
      }
    } catch (error) {
      meta.errors = (meta.errors || 0) + 1
      console.warn(`[AsyncTaskTracker] Poll failed for ${taskId} (${meta.errors}/${MAX_CONSECUTIVE_ERRORS}):`, error.message)
      if (meta.errors >= MAX_CONSECUTIVE_ERRORS) {
        onMessageRef.current({
          role: 'participant',
          participantId: meta.roleId,
          content: `Task polling stopped: ${error.message}`,
          timestamp: Date.now(),
          metadata: { taskId, type: 'failed', conversationId: meta.conversationId },
        })
        stopPolling(taskId)
      }
    }
  }, [persist, stopPolling])

  const startPolling = useCallback((taskId, meta) => {
    if (tasksRef.current[taskId]) return
    tasksRef.current[taskId] = { ...meta, outputIndex: meta.outputIndex || 0, startedAt: meta.startedAt || Date.now(), errors: 0 }
    persist()
    poll(taskId)
    intervalsRef.current[taskId] = setInterval(() => poll(taskId), POLL_INTERVAL)
  }, [persist, poll])

  // Restore active tasks from localStorage once ready (after history loads)
  useEffect(() => {
    if (!ready) return
    try {
      const saved = storageRef.current.getItem(STORAGE_KEY)
      if (saved) {
        const tasks = JSON.parse(saved)
        let hasExpired = false
        for (const [taskId, meta] of Object.entries(tasks)) {
          if (Date.now() - (meta.startedAt || 0) > MAX_TASK_AGE) {
            hasExpired = true
            continue
          }
          startPolling(taskId, meta)
        }
        if (hasExpired) persist()
      }
    } catch (e) {
      console.warn('[AsyncTaskTracker] Failed to restore tasks:', e.message)
    }
    return () => {
      for (const id of Object.keys(intervalsRef.current)) {
        clearInterval(intervalsRef.current[id])
      }
    }
  }, [ready, startPolling])

  const track = useCallback((taskId, { roleId, displayName, conversationId }) => {
    startPolling(taskId, { roleId, displayName, conversationId, outputIndex: 0 })
  }, [startPolling])

  return { track }
}
